import { fetchJson } from '../legal-board/api';

const base = '/public-utility';

export const fetchPublicUtilityOverview = (userId) => fetchJson(`${base}/overview`, { query: { userId } });
export const fetchPublicUtilityBadge = (userId) => fetchJson(`${base}/badge`, { query: { userId } });

export const updateRequirements = ({ userId, requirements }) => fetchJson(`${base}/requirements`, { method: 'PUT', body: { userId, requirements } });
export const recordAssembly = ({ userId, date }) => fetchJson(`${base}/assembly`, { method: 'POST', body: { userId, date } });
export const applicationAction = ({ userId, action, date, reference, note }) => fetchJson(`${base}/application`, { method: 'POST', body: { userId, action, date, reference, note } });
export const regimeAction = ({ userId, action, date }) => fetchJson(`${base}/regime`, { method: 'POST', body: { userId, action, date } });
export const councilAction = ({ userId, action, date }) => fetchJson(`${base}/council`, { method: 'POST', body: { userId, action, date } });
export const updateSettings = ({ userId, settings }) => fetchJson(`${base}/settings`, { method: 'PUT', body: { userId, settings } });
export const filingAction = ({ userId, year, obligation, action, date, reference }) => fetchJson(`${base}/filings/${year}/${obligation}`, { method: 'POST', body: { userId, action, date, reference } });

export const fetchReport = ({ userId, year }) => fetchJson(`${base}/reports/${year}`, { query: { userId } });

/**
 * The tab fetches the memòries it links to while it is open, so a memòria
 * opens with its data instead of a spinner. An entry is dropped when its
 * memòria is closed: what was saved there comes fresh the next time.
 */
const reportCache = new Map();
const reportKey = ({ userId, year }) => `${userId}:${year}`;

export function preloadReport({ userId, year }) {
    const key = reportKey({ userId, year });
    if (reportCache.has(key)) return reportCache.get(key).promise;
    const entry = { payload: null, promise: null };
    entry.promise = fetchReport({ userId, year })
        .then((payload) => {
            entry.payload = payload;
            return payload;
        })
        .catch((error) => {
            reportCache.delete(key);
            throw error;
        });
    reportCache.set(key, entry);
    return entry.promise;
}

export const cachedReport = ({ userId, year }) => reportCache.get(reportKey({ userId, year }))?.payload || null;
export const forgetReport = ({ userId, year }) => reportCache.delete(reportKey({ userId, year }));
export const saveReport = ({ userId, year, texts, excludedEvents }) => fetchJson(`${base}/reports/${year}`, { method: 'PUT', body: { userId, texts, excludedEvents } });

export const fetchDonors = ({ userId, year }) => fetchJson(`${base}/donors/${year}`, { query: { userId } });
export const saveIdentity = ({ userId, identity }) => fetchJson(`${base}/identities`, { method: 'PUT', body: { userId, identity } });
export const deleteIdentity = ({ userId, id }) => fetchJson(`${base}/identities/${id}`, { method: 'DELETE', query: { userId } });
export const addManualDonation = ({ userId, donation }) => fetchJson(`${base}/manual-donations`, { method: 'POST', body: { userId, donation } });
export const deleteManualDonation = ({ userId, id }) => fetchJson(`${base}/manual-donations/${id}`, { method: 'DELETE', query: { userId } });

async function download(path, query, filename) {
    const blob = await fetchJson(path, { query, responseType: 'blob' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Let the browser start reading the blob before releasing it.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const downloadReportPdf = ({ userId, year }) => download(`${base}/reports/${year}/pdf`, { userId }, `memoria-activitats-${year}.pdf`);
export const downloadReportText = ({ userId, year }) => download(`${base}/reports/${year}/txt`, { userId }, `memoria-activitats-${year}.txt`);
export const downloadIncomePdf = ({ userId, year }) => download(`${base}/income/${year}/pdf`, { userId }, `ingressos-${year}.pdf`);
export const downloadApplicationLetter = ({ userId }) => download(`${base}/application/letter`, { userId }, 'escrit-sollicitud-utilitat-publica.pdf');
export const downloadCouncilLetter = ({ userId }) => download(`${base}/application/council-letter`, { userId }, 'sollicitud-informe-ajuntament.pdf');
export const downloadAssemblyCertificate = ({ userId }) => download(`${base}/assembly/certificate`, { userId }, 'certificat-acord-assemblea.pdf');
export const downloadModel182 = ({ userId, year, nif, contactName, phone }) => download(`${base}/donors/${year}/model182`, { userId, contactName, phone }, `182_${nif || 'colla'}_${year}.txt`);
export const downloadCertificates = ({ userId, year, key }) => download(`${base}/donors/${year}/certificates`, { userId, key }, `certificats-donatius-${year}.pdf`);

// The Generalitat's e-filing takes attachments up to 5 MB
export function readSignedPdf(file) {
    if (!file || !/\.pdf$/i.test(file.name) || file.size === 0) {
        return Promise.reject(new Error('Tria la memòria signada, en PDF.'));
    }
    if (file.size > 5 * 1024 * 1024) {
        return Promise.reject(new Error('La Generalitat no accepta fitxers de més de 5 MB.'));
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('No s’ha pogut llegir el fitxer. Torna’l a triar.'));
        reader.onabort = reader.onerror;
        reader.onload = () => resolve({ name: file.name, data: String(reader.result).split(',')[1] });
        reader.readAsDataURL(file);
    });
}

export const uploadSignedReport = ({ userId, year, file }) => fetchJson(`${base}/reports/${year}/signed`, { method: 'POST', body: { userId, file } });
export const removeSignedReport = ({ userId, year }) => fetchJson(`${base}/reports/${year}/signed`, { method: 'DELETE', query: { userId } });
export const downloadSignedReport = ({ userId, year }) => download(`${base}/reports/${year}/signed`, { userId }, `memoria-activitats-${year}-signada.pdf`);
